import * as yup from 'yup';
import * as React from 'react';
import * as Formik from 'formik';
import { FormikHelpers as FormikActions } from 'formik/dist/types';
import { Alert, Button } from 'reactstrap';
import {
  CredentialField,
  CredentialRequest,
  CredentialSchema,
  CredentialSecret,
  YupValidatorType,
} from '../../../../types';
import {
  getDropdownOptions,
  getSubfields,
  getObjVal,
  HTTP_STATUS,
  readTextFile,
  STATIC_SRC,
} from '../../../../constants';
import FormikInputField from '../../../../widgets/FormikInputField';
import MultiInputField from '../../../../widgets/MultiInputField';
import { APIError } from '../../../../APIError';
import LoadingIndicator from '../../../../widgets/LoadingIndicator';
import Icon from '../../../../widgets/Icons';
import { ACCOUNT_TYPE_TOOLTIP, ACCOUNT_PURPOSE_TEXT } from '../constants';

export const REQUIRED_FIELD_ERROR_MESSAGE: string = 'This field is required';

export const NAME_FIELD: CredentialField = {
  name: 'name',
  display_name: 'Account Name',
  description:
    'Enter a unique name that is easy for you to identify across all your cloud accounts.',
  sensitive: false,
  data_type: 'string',
  required: true,
  order: -1,
  default: '',
  options: [],
  validators: [
    {
      data_type: 'string',
      validator_name: 'required',
      error_message: REQUIRED_FIELD_ERROR_MESSAGE,
    },
  ],
};

export const NO_FIELD_INFO = (name: string) => (
  <div className="description" key={`${name}-description`}>
    <p>
      Setting up {name} does not require you to supply Arctic Wolf with any
      information. Arctic Wolf will assign a unique token for your account.
    </p>
    <p>
      Once you have submitted this credential request, it will appear in a
      'Connection Pending' state. Your Concierge Security Team will activate the
      sensor, and the token you will need to enter into {name} will be displayed
      in the connected accounts table following activation.
    </p>
  </div>
);

/**
 * Used as the validator function passed to yup.mixed().test()
 * @param {string} path - the path to the field that is being validated
 */
const jsonFileValidator = (path: string) => (file: File | null) =>
  new Promise<boolean | yup.ValidationError>(async (resolve, reject) => {
    if (!file) {
      // don't validate if nothing is selected
      return resolve(true);
    }
    try {
      const data = await readTextFile(file);
      JSON.parse(data);
      return resolve(true);
    } catch (err) {
      let message;
      if (err instanceof SyntaxError) {
        message = 'JSON file syntax error: ' + err.message;
      } else {
        message = err.message;
      }
      return reject(new yup.ValidationError(message, file, path));
    }
  });

export const YUP_JSON_SCHEMA = (name: string) =>
  yup.mixed().test(
    'isJSON',
    'Invalid JSON', // used when the validator rejects without ValidationError
    jsonFileValidator(name),
  );

export const makeYupSchema = (
  fields: Array<CredentialField>,
  isEdit: boolean,
) => {
  const schemaShape = fields.reduce<{
    [field: string]: yup.StringSchema | yup.AnySchema;
  }>((shape, field: CredentialField) => {
    const { name, data_type, required, sensitive } = field;
    const validators = field.validators || [];

    if (validators.length || required) {
      let fieldValidator: yup.StringSchema | yup.AnySchema;

      switch (data_type) {
        case 'json':
          fieldValidator = YUP_JSON_SCHEMA(name);
          if (isEdit) {
            // In 'edit' mode, JSON fields are always allowed to be 'null' (reuse existing value), regardless
            // of secret/required.  If they are required, they are also not allowed to be 'undefined' (not set
            // to reuse, but no file specified)
            fieldValidator = fieldValidator.notOneOf(
              [undefined],
              REQUIRED_FIELD_ERROR_MESSAGE,
            );
          } else {
            // In 'create' mode, JSON fields can never be null.  If they are required, they cannot be 'undefined'
            // (no value supplied), but otherwise can be anything.
            if (required) {
              fieldValidator = fieldValidator.required(
                REQUIRED_FIELD_ERROR_MESSAGE,
              );
            }
          }
          break;
        case 'checkbox':
          fieldValidator = yup.object();
          break;
        case 'fieldchoice':
          fieldValidator = yup.string();
          break;
        default:
          // "string" or "select"
          fieldValidator = yup.string();
          if (sensitive && isEdit) {
            if (required) {
              fieldValidator = fieldValidator
                // Formik does a strange/undesirable pre-processing of values and turns empty string
                // into undefined, which is allowed by .nullable when we don't want it to be
                .defined(REQUIRED_FIELD_ERROR_MESSAGE)
                .nullable();
            } else {
              fieldValidator = fieldValidator.notRequired().nullable();
            }
          } else if (required) {
            // TODO: move 'required' into a schema's validators array
            fieldValidator = fieldValidator.required(
              REQUIRED_FIELD_ERROR_MESSAGE,
            );
          }
      }

      validators.forEach((validator: YupValidatorType) => {
        let { validator_name, validator_value, error_message } = validator;

        // wrap input in regex for string.matches()
        if (validator_name === 'matches' && validator_value) {
          validator_value = new RegExp(validator_value);
        }

        // chaining them
        if (validator_value) {
          //@ts-ignore: technically unsafe - relies on data from server; also hard to dynamically type
          fieldValidator = fieldValidator[validator_name](
            validator_value,
            error_message,
          );
        } else {
          //@ts-ignore: technically unsafe - relies on data from server; also hard to dynamically type
          fieldValidator = fieldValidator[validator_name](error_message);
        }
      });

      shape[name] = fieldValidator;
    }
    return shape;
  }, {});

  //Handle the 'at least one of' requirement for the fieldchoice subfields
  fields.forEach((field) => {
    const { data_type, required } = field;
    if (data_type !== 'fieldchoice') {
      return;
    }
    const subfields = getSubfields(field);
    if (!required) {
      subfields?.forEach((subfield) => {
        if (subfield in schemaShape) {
          schemaShape[subfield] = schemaShape[subfield].notRequired();
        }
      });
    } else {
      subfields?.forEach((subfield, index) => {
        if (!(subfield in schemaShape)) {
          schemaShape[subfield] = yup.string();
        }
        schemaShape[subfield] = (
          schemaShape[subfield] as yup.StringSchema
        ).when(field.name, {
          is: subfield,
          then: (schema) => schema.required(REQUIRED_FIELD_ERROR_MESSAGE),
          otherwise: (schema) => schema.notRequired(),
        });
      });
    }
  });

  return yup.object().shape(schemaShape);
};

export const makeInitialValues = (
  schemaType: string,
  fields: Array<CredentialField>,
  credential: CredentialSecret | null,
) => {
  const initialValues: Record<string, string | null> = {};
  const isEdit = !!credential;
  if (isEdit) {
    // For every available field we want to pre-populate it with the existing
    // credential value, except for 'sensitive' fields where we will set them
    // to 'null' as a default, which indicates "reuse existing value"
    fields.forEach((field) => {
      if (field.sensitive || field.data_type === 'json') {
        initialValues[field.name] = null;
      } else if (field.data_type === 'checkbox') {
        initialValues[field.name] = field.default;
      } else if (field.data_type === 'string' || field.data_type === 'select') {
        initialValues[field.name] = getObjVal(
          credential,
          'secrets.' + field.name,
          field.default,
        );
      } else if (field.data_type === 'fieldchoice') {
        const subfields = getSubfields(field);
        initialValues[field.name] = getObjVal(
          credential,
          'secrets.' + field.name,
          subfields ? subfields[0] : field.default ?? '',
        );
      }
    });
    initialValues.name = getObjVal(credential, 'name', '');
  } else {
    // For brand new credentials, just initialize all text fields to empty string
    fields.forEach((field) => {
      if (field.data_type === 'string' || field.data_type === 'select') {
        const options = getDropdownOptions(field, schemaType);
        if (options) {
          initialValues[field.name] = options[0].value;
        } else {
          initialValues[field.name] = field.default ?? '';
        }
      } else if (field.data_type === 'checkbox') {
        initialValues[field.name] = field.default;
      } else if (field.data_type === 'fieldchoice') {
        const subfields = getSubfields(field);
        initialValues[field.name] = subfields
          ? subfields[0]
          : field.default ?? '';
      }
    });
  }

  return initialValues;
};

const processChoicefields = (fields: CredentialField[]) => {
  const fieldchoiceFields = fields.filter(
    (field) => field.data_type === 'fieldchoice',
  );

  let dependentToFieldchoice: Record<string, string> = {};
  let fieldchoiceToDependents: Record<string, CredentialField[]> = {};
  fieldchoiceFields.forEach((field) => {
    fieldchoiceToDependents[field.name] = [];
    const choices: string[] = field.validators.find(
      (v) => v.validator_name === 'oneOf',
    )?.validator_value;
    choices?.forEach((choice) => {
      dependentToFieldchoice[choice] = field.name;
    });
  });

  let newFields: CredentialField[] = [];
  fields.forEach((field) => {
    const dependingField = dependentToFieldchoice[field.name];
    if (dependingField !== undefined) {
      fieldchoiceToDependents[dependingField].push(field);
    } else {
      newFields.push(field);
    }
  });

  return { prunedFields: newFields, dependentFields: fieldchoiceToDependents };
};

/**
 * Return an array of CredentialField objects (with the 'name' field added first)
 *   and the list of <Field/> elements to render on the form
 */
export const useFormikFields = (schema: CredentialSchema, isEdit: boolean) =>
  React.useMemo(() => {
    const fields = [NAME_FIELD, ...(schema.fields || [])];
    const { prunedFields, dependentFields } = processChoicefields(fields);
    const fieldElements = prunedFields.map(
      (field: CredentialField, index: number) => {
        if (field.data_type === 'fieldchoice') {
          return (
            <MultiInputField
              field={field}
              subfields={dependentFields[field.name]}
              schema={schema}
              isEdit={isEdit}
              key={index}
            />
          );
        } else {
          const options = getDropdownOptions(field, schema.name);
          return (
            <Formik.Field
              component={FormikInputField}
              options={options}
              key={index}
              name={field.name}
              label={field.display_name}
              description={field.description}
              dataType={options ? 'select' : field.data_type}
              sensitive={field.sensitive}
              isEdit={isEdit}
              paddingBottom={10}
              noAutoComplete
            />
          );
        }
      },
    );
    if (fieldElements.length === 1) {
      fieldElements.push(NO_FIELD_INFO(schema.display_name));
    }
    return { fields, fieldElements };
  }, [schema, isEdit]);

/**
 * Create the props used by <Formik>:
 *   validationSchema, initialValues, onSubmit
 */
export const useFormikProps = (
  schemaType: string,
  fields: Array<CredentialField>,
  credential: CredentialSecret | null,
  onSubmitForm: (
    request: CredentialRequest,
    setStatus: (error: APIError) => any,
  ) => Promise<any>,
) => {
  const initialValues = makeInitialValues(schemaType, fields, credential);
  const validationSchema = makeYupSchema(fields, !!credential);

  const jsonFields = fields.filter((field) => field.data_type === 'json');
  const onSubmit = async (
    values: {
      [key: string]: any;
    },
    {
      setStatus,
      setSubmitting,
    }: Pick<FormikActions<any>, 'setStatus' | 'setSubmitting'>,
  ) => {
    setStatus(null); // clear previous submission errors
    const { name, ...secrets } = values;

    for (const jsonField of jsonFields) {
      const fieldName = jsonField.name;
      if (secrets[fieldName] instanceof File) {
        secrets[fieldName] = await readTextFile(secrets[fieldName]);
      }
    }

    // any error response should be handled within onSubmitForm
    await onSubmitForm({ name, secrets, type: schemaType }, setStatus);
    setSubmitting(false);
  };

  return {
    initialValues,
    validationSchema,
    onSubmit,
  };
};

/**
 * Build the rest of the form that doesn't depend on the schema
 *  e.g. error alert, button and loading indicators
 */
export const FormHelpers = (props: { onCancel: () => void }) => {
  const { status, isSubmitting, isValid, dirty } = Formik.useFormikContext();

  return (
    <>
      {status && (
        <Alert
          className="my-3"
          color={
            status.status === HTTP_STATUS.BAD_REQUEST ? 'warning' : 'danger'
          }
        >
          {status.message || `${status}`}
        </Alert>
      )}

      <div className="d-flex my-4 justify-content-between">
        <Button
          outline
          color="primary px-4"
          onClick={props.onCancel}
          disabled={isSubmitting}
          data-cy="back"
        >
          Back
        </Button>
        <Button
          color="primary px-4"
          type="submit"
          // new behaviour, possibly better. Submit will be disabled even when user reverts change back to the original
          // In formik 1.5.8 - isValid also checks if the form is pristine
          disabled={isSubmitting || !isValid || !dirty}
          data-cy="submit"
        >
          {isSubmitting && <LoadingIndicator light className="mr-3" />}
          Submit to CST
        </Button>
      </div>
    </>
  );
};

type Props = {
  onCancel: () => void;
  onSubmit: (
    request: CredentialRequest,
    setStatus: (error: APIError) => any,
  ) => Promise<any>;
  schema: CredentialSchema;
  credential: CredentialSecret | null;
};

const ConnectedAccountForm = (props: Props) => {
  const { schema, credential, onSubmit } = props;
  const { fields, fieldElements } = useFormikFields(schema, !!credential);
  const formikProps = useFormikProps(schema.name, fields, credential, onSubmit);

  return (
    <>
      <div className="d-flex my-3">
        <img src={`${STATIC_SRC}${schema.image}`} alt={schema.display_name} />
        <div className="align-self-center ml-4">
          <a
            href={schema.setup_guide}
            target="_blank"
            rel="noopener noreferrer"
            title="Open Configuration Guide in a new window"
            data-cy="guide"
          >
            <Icon.ICircle /> View configuration guide
          </a>
        </div>
      </div>
      <Formik.Formik {...formikProps}>
        {/* fix this TS bug by upgrading react types https://github.com/jaredpalmer/formik/issues/2120
      //@ts-ignore  */}
        <Formik.Form className="connected-account-form m-3">
          <label>
            Account Type
            {ACCOUNT_TYPE_TOOLTIP}
          </label>
          <div className="mb-4">
            {ACCOUNT_PURPOSE_TEXT[schema.purpose] ?? ''}
          </div>

          {fieldElements}
          <FormHelpers onCancel={props.onCancel} />
        </Formik.Form>
      </Formik.Formik>
    </>
  );
};

export default ConnectedAccountForm;
